import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Component, Inject, LOCALE_ID, NgZone, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  ResolveEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { AjaxHttpClientAdapter } from 'breeze-client/adapter-ajax-httpclient';
import { DataServiceWebApiAdapter } from 'breeze-client/adapter-data-service-webapi';
import { ModelLibraryBackingStoreAdapter } from 'breeze-client/adapter-model-library-backing-store';
import { UriBuilderJsonAdapter } from 'breeze-client/adapter-uri-builder-json';
import { environment } from 'environments/environment';
import _ from 'lodash-es';
import { Observable, asyncScheduler, concat, interval } from 'rxjs';
import { distinctUntilChanged, filter, first, map, observeOn } from 'rxjs/operators';
import { AuthService } from './core/auth/auth.service';
import { BusyService } from './core/services';
import { TelemetryService } from './core/services/telemetry.service';
import { trimBasePrefixFromPath } from './core/utils/route-helper';
import { MessageBoxService } from './shared/messagebox';
import { SharedModule } from './shared/shared.module';
import { MessageSnackBarData, MessageSnackbarComponent } from './shared/snackbar/message-snackbar.component';
import { Settings } from 'luxon';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterOutlet, CommonModule, SharedModule],
})
export class AppComponent implements OnInit {
  isBusy$: Observable<boolean>;
  private telemetryService: TelemetryService;

  /**
   * Constructor
   */
  constructor(
    http: HttpClient,
    @Inject(LOCALE_ID) localId: string,
    busyService: BusyService,
    private zone: NgZone,
    private router: Router,
    private titleService: Title,
    private authService: AuthService,
    appRef: ApplicationRef,
    updates: SwUpdate,
    messageBox: MessageBoxService,
    snackBar: MatSnackBar,
  ) {
    // Configure Breeze adapters
    ModelLibraryBackingStoreAdapter.register();
    UriBuilderJsonAdapter.register();
    AjaxHttpClientAdapter.register(http);
    DataServiceWebApiAdapter.register();

    // locale for Luxon DateTime
    Settings.defaultLocale = localId;

    this.telemetryService = new TelemetryService(router, authService);
    this.telemetryService.logEvent('AppComponentLoad');

    this.isBusy$ = busyService.busyState$.pipe(
      observeOn(asyncScheduler),
      map((busyState) => busyState.isBusy),
      distinctUntilChanged(),
    );

    // Service worker
    if (updates.isEnabled) {
      updates.versionUpdates.subscribe((event) => {
        if (event.type === 'VERSION_READY') {
          snackBar.openFromComponent<MessageSnackbarComponent, MessageSnackBarData>(MessageSnackbarComponent, {
            verticalPosition: 'top',
            data: {
              label: `A new version of ${environment.appName} has been detected and you must refresh your browser.`,
              action: 'Refresh',
              onAction: () => updates.activateUpdate().then(() => location.reload()),
            },
          });
        }
      });

      updates.unrecoverable.subscribe((event) => {
        messageBox.showMessage(
          'Fatal Error',
          'An error occurred that we cannot recover from:\n' + event.reason + '\n\nPlease reload the page',
          ['Ok]'],
        );
      });

      const appIsStable$ = appRef.isStable.pipe(first((isStable) => isStable === true));
      const updateInterval$ = interval(environment.updateInterval * 60 * 1000);
      concat(appIsStable$, updateInterval$).subscribe(() => {
        updates.checkForUpdate();
      });
    }
  }

  ngOnInit(): void {
    this.setupApplicationInsightsRouting();
    this.setupWindowRouting();
    this.setPageTitles();
  }

  navigateByUrl(url: string, token: string): Promise<boolean> {
    url = trimBasePrefixFromPath(url);
    this.authService.accessToken = token;
    return this.zone.run(() => this.router.navigateByUrl(url));
  }

  private getActivatedComponent(snapshot: ActivatedRouteSnapshot): any {
    if (snapshot.firstChild) {
      return this.getActivatedComponent(snapshot.firstChild);
    }
    return snapshot.component;
  }
  private setupApplicationInsightsRouting(): void {
    this.router.events.pipe(filter((event): event is ResolveEnd => event instanceof ResolveEnd)).subscribe((event) => {
      const activatedComponent = this.getActivatedComponent(event.state.root);
      if (activatedComponent) {
        this.telemetryService.logPageView(activatedComponent.name, event.urlAfterRedirects);
      }
    });
  }

  /** Expose window.navigateByUrl function to be called by Puppeteer */
  private setupWindowRouting(): void {
    const win = window as any;
    win.navigateByUrl = this.navigateByUrl.bind(this);
  }

  /** Set the page title according to the current route */
  private setPageTitles(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          let job = null;
          while (route.firstChild) {
            route = route.firstChild;
            job = job || route.snapshot.data.job;
          }
          if (route.snapshot.data['title']) {
            // put { data: { title: 'my title' }} on routes for custom title
            routeTitle = route.snapshot.data['title'];
          } else {
            // default is to use the route path as title
            routeTitle = _.startCase(route.snapshot.routeConfig.path || route.parent.snapshot.routeConfig.path);
          }
          if (job) {
            routeTitle = job.shortName + ' - ' + routeTitle;
          }
          return routeTitle;
        }),
      )
      .subscribe((title: string) => {
        if (title) {
          this.titleService.setTitle(`${environment.appName} - ${title}`);
        }
      });
  }
}
